import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import axios from "axios";
import { Search, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { API_BASE } from "../config";

interface TeamIncomeItem {
  _id: string;
  emailOwner: string;
  emailMember: string;
  income: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ApiResponse {
  status: string;
  cached: boolean;
  data: {
    teamIncome: TeamIncomeItem[];
    pagination: PaginationInfo;
  };
}

const PlatinumTeamIncome: React.FC = () => {
  const [teamIncomeData, setTeamIncomeData] = useState<TeamIncomeItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 20,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useLayoutEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [search, pagination.limit]);

  const fetchTeamIncomeData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pagination.currentPage),
        limit: String(pagination.limit),
        searchTerm: search,
      });
      const response = await axios.get<ApiResponse>(`${API_BASE}/api/platinum/admin/team-income?${params.toString()}`);
      setTeamIncomeData(response.data.data.teamIncome);
      setPagination(response.data.data.pagination);
      setError(null);
    } catch (err) {
      setError("Failed to load team income data. Please try again.");
      console.error("Error fetching team income data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, search]);

  useEffect(() => {
    fetchTeamIncomeData();
  }, [fetchTeamIncomeData]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const handleRefresh = () => {
    fetchTeamIncomeData();
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 pb-20">
      <div className="mb-6 md:mb-8 text-center md:text-left">
        <h1 className="text-2xl md:text-3xl font-bold text-[#523680] mb-2">Platinum Team Income</h1>
        <p className="text-sm md:text-base text-gray-500 font-medium">Monitor and manage all platinum team income distributions</p>
      </div>

      {/* Controls Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm mb-6 space-y-4">
        <div className="relative w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by owner/member..."
            className="block w-full pl-9 pr-3 py-2.5 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-[#523680]/20 text-sm font-medium"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            className="p-2 bg-[#523680]/5 border-none rounded-lg hover:bg-[#523680]/10 transition-colors flex items-center justify-center text-[#523680]"
            onClick={handleRefresh}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="flex justify-between mb-4 text-sm text-gray-600">
        <div>
          Page {pagination.currentPage} — {teamIncomeData.length} rows (total: {pagination.totalRecords})
        </div>
      </div>

      {/* Team Income Cards - List View */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-lg text-red-600 text-center">
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          {teamIncomeData.map((item) => (
            <div key={item._id} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 overflow-hidden">
              <div className="p-5">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <div className="flex-grow">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Owner</p>
                        <h3 className="font-bold text-[#523680] truncate">{item.emailOwner}</h3>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Member</p>
                        <h4 className="font-medium text-gray-700 truncate">{item.emailMember}</h4>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3">
                      <p className="text-[10px] text-gray-500 font-medium italic">
                        {formatDate(item.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-50 text-green-700 px-5 py-2.5 rounded-xl font-black text-xl self-start md:self-auto shadow-sm border border-green-100/50">
                    ${parseFloat(item.income).toFixed(2)}
                  </div>
                </div>
                <div className="text-[10px] mt-4 pt-3 border-t border-gray-50 flex justify-between text-gray-300 font-mono">
                  <span>ID: {item._id}</span>
                  {item.updatedAt !== item.createdAt && (
                    <span>Updated: {formatDate(item.updatedAt)}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {!isLoading && teamIncomeData.length === 0 && (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-600">No team income records found matching your criteria.</p>
        </div>
      )}

      {/* Pagination */}
      <div className="flex flex-col md:flex-row justify-between items-center mt-10 gap-6">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-tight order-2 md:order-1">
          Page {pagination.currentPage} / {pagination.totalPages}
        </div>
        <div className="flex items-center space-x-2 order-1 md:order-2">
          <select
            value={pagination.limit}
            onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-[#523680] bg-white"
          >
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
          <button
            className={`p-2 rounded-xl border transition-all ${!pagination.hasPrevPage ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'hover:bg-white text-[#523680] border-[#523680]/20 shadow-sm'}`}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrevPage}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {/* Page numbers */}
          <div className="flex space-x-1.5 overflow-x-auto no-scrollbar max-w-[150px] sm:max-w-none px-1">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNumber;
              if (pagination.totalPages <= 5) pageNumber = i + 1;
              else if (pagination.currentPage <= 3) pageNumber = i + 1;
              else if (pagination.currentPage >= pagination.totalPages - 2) pageNumber = pagination.totalPages - 4 + i;
              else pageNumber = pagination.currentPage - 2 + i;

              return (
                <button
                  key={i}
                  className={`h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-xl font-bold text-xs md:text-sm transition-all shrink-0 ${pagination.currentPage === pageNumber
                    ? 'bg-[#523680] text-white shadow-lg'
                    : 'border border-gray-100 hover:border-[#523680]/20 text-[#523680] bg-white'
                    }`}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            className={`p-2 rounded-xl border transition-all ${!pagination.hasNextPage ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'hover:bg-white text-[#523680] border-[#523680]/20 shadow-sm'}`}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatinumTeamIncome;